import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { api } from "helpers/axios";
import { errorMessage, isTrue, scrollInto, scrollToTop } from "helpers/common";
import { toast } from "helpers/toast";
import * as Sentry from "@sentry/react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import ErrorAlert from "components/ui/ErrorAlert";
import { Banner } from "components/ui";
import { useTranslation } from "translation/useTranslation";
import Link from "components/ui/Link";
import { isSameModule, isDbModule } from "helpers/module";
import { Button, Input } from "components/ui";

function DummyField() {
  // used to have the same width as the normal field
  const { editableTranslate } = useTranslation();

  return <>
    <div className="d-inline-flex align-items-center mr-3">
      <span className="toggle-switch on-off mr-1">
        <input type="checkbox" value={true} readOnly={false} disabled={false} data-testid="dummy-field-toggle" />
        <label></label>
      </span>
      <span>{editableTranslate('field_configuration.on', 'Aktiv')}</span>
    </div>
    <Input data-testid="dummy-field-input" size="sm" className={`d-inline-block max-w-200`} type="text" disabled={true} maxLength={255} style={{ marginRight: '45px' }} />
  </>;
}

export default function FieldConfiguration() {
  const { translate, editableTranslate } = useTranslation();

  const [error, setError] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [modules, setModules] = useState([]);

  const [allFields, setAllFields] = useState([]);

  const [showSection, setShowSection] = useState({
    fixed: true,
    state: true,
    calculation: true,
    lk: true,
    free: true
  });

  const headerRef = useRef(null);

  const theadRef = useRef(null);

  const fixedChecked = {
    standard: ['last_name', 'first_name', 'employee_number', 'working_hours', 'exclude_from_payout', 'balanced_scorecard_template', 'budget', 'group'],
    state: ['last_name', 'first_name', 'employee_number', 'balanced_scorecard_template', 'group']
  };

  const moduleFieldKey = (module) => isDbModule(module) ? 'state' : 'standard';
  const fieldSupportsModule = (field, module) => field?.modules?.some(moduleKey => isSameModule(moduleKey, module));

  const [
    filteredFixedFields,
    filteredStateEmployeeFields,
    filteredCalculationRelatedFields,
    filteredLkFields,
    filteredFreeFields
  ] = useMemo(() => {
    const fixedFields = allFields.filter(field => field.field_type === 'fixed' && !field.is_balanced_scorecard_specific);
    const stateFields = allFields.filter(field => {
      let hasDuplicate = false;
      for (const f of allFields) {
        if (f.slug === field.slug && f.hash_id !== field.hash_id) {
          hasDuplicate = true;
          break;
        }
      }

      return field.field_type === 'conditional' && field?.source === 'state_employee_config' && field?.is_importable && !hasDuplicate;
    });
    const calculationFields = allFields.filter(field => ['calculation_related', 'system', 'conditional'].includes(field.field_type) && field?.source !== 'state_employee_config');
    const lkFields = allFields.filter(field => field.field_type === 'fixed' && field.is_balanced_scorecard_specific);
    const freeFields = allFields.filter(field => field.field_type === 'free');

    return [
      fixedFields,
      stateFields,
      calculationFields,
      lkFields,
      freeFields
    ];
  }, [allFields]);

  const { register, getValues, setValue, watch } = useForm({
    defaultValues: {
      fields: {}
    }
  });

  const watchFields = watch('fields');

  useEffect(() => {
    fetchLicense();
    fetchFields();

    const headerEl = headerRef.current;
    if (!headerEl) return;

    // const theadEl = theadRef.current;
    // if (!theadEl) return;

    const handleScrollTable = () => {
      const headerParentEl = headerEl.parentElement;
      // const parentEl = theadEl.parentElement;
      // const coordinates = parentEl.getBoundingClientRect();
      const headerCoordinates = headerParentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;
      if (headerCoordinates.y < navHeight) {
        headerEl.style.transform = `translate3d(0, ${-headerCoordinates.y + navHeight}px, 0)`;
        headerEl.classList.add('border-bottom');
        // theadEl.style.transform = `translate3d(0, ${-headerCoordinates.y + navHeight - 1}px, 0)`;
      } else {
        headerEl.style.transform = 'translate3d(0,0,0)';
        headerEl.classList.remove('border-bottom');
        // theadEl.style.transform = 'translate3d(0,0,0)';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, []);

  useEffect(() => {
    [...filteredFixedFields, ...filteredStateEmployeeFields, ...filteredCalculationRelatedFields, ...filteredLkFields, ...filteredFreeFields].forEach(f => {
      setValue(`fields.${f.hash_id}`, {
        standard_enabled: f?.standard_enabled,
        standard_custom_name: f?.standard_custom_name,
        state_enabled: f?.state_enabled,
        state_custom_name: f?.state_custom_name
      }, { shouldDirty: true });
    });
  }, [filteredFixedFields, filteredStateEmployeeFields, filteredCalculationRelatedFields, filteredLkFields, filteredFreeFields]);

  const handleSaveConfiguration = async () => {
    setError(null);
    setLoading(true);

    const values = getValues();

    const fields = [];
    for (const k in values.fields) {
      fields.push({
        hash_id: k,
        standard_enabled: isTrue(values.fields[k].standard_enabled) ? true : false,
        standard_custom_name: values.fields[k].standard_custom_name,
        state_enabled: isTrue(values.fields[k].state_enabled) ? true : false,
        state_custom_name: values.fields[k].state_custom_name
      });
    }

    try {
      await api.patch(`/settings/fields/configuration`, { fields });

      setLoading(false);

      fetchFields(false);

      toast.success(translate('form.success.saved'));
    } catch (err) {
      setLoading(false);
      setError(errorMessage(err, 'save field configuration'));
      scrollToTop();
      Sentry.captureException(err);
    }
  };

  const handleReadonly = (e) => {
    e.preventDefault();
    return false;
  };

  const fetchFields = async (loading = true) => {
    if (loading) setFetchLoading(true);
    try {
      const { data, status } = await api.get('/settings/fields?all_mode=true');
      if (status === 200) {
        setAllFields(data.data.filter(f => f.is_active));
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch fields'));
      Sentry.captureException(err);
    }
    if (loading) setFetchLoading(false);
  };

  const fetchLicense = async () => {
    try {
      const { data, status } = await api.get('/common/license/state');
      if (status === 200) {
        setModules(data?.data?.restrictions?.modules || []);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch license'));
      Sentry.captureException(err);
    }
  };

  const showHideSection = (section) => {
    setShowSection(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 pb-4 px-4 content-card`}>
          <div ref={headerRef} className="d-flex justify-content-between bg-white pb-3 pt-4 z-2 position-relative">
            <div>
              <h4>{editableTranslate('global.menu.settings.field_configuration', 'Feldkonfiguration')}</h4>
              <p className="mb-1">{editableTranslate('field_configuration.description')}</p>
            </div>
            <div className="text-nowrap">
              <Button variant="primary" loading={loading} onClick={handleSaveConfiguration} data-testid="save-button">{editableTranslate('button.save')}</Button>
            </div>
          </div>
          <Banner intent="info" className="mb-3" data-testid="field-configuration-info">
            <span className="d-block">{editableTranslate('field_configuration.banner_text', 'Hier legen Sie fest, welche Felder pro Modul verfügbar sind.')}</span>
            <span className="d-block">{editableTranslate('field_configuration.go_to_field_display_options_description', 'Achtung: Um die Sichtbarkeit von aktivierten Feldern zu verwalten, gehen Sie zu')} <Link data-testid="display-options-link" to="/settings/display-options">{editableTranslate('global.menu.settings.display_options')}</Link>.</span>
          </Banner>
          <div className="settings-form settings-employee">
            {
              error && <div>
                <ErrorAlert error={error} onClose={() => setError(null)} />
              </div>
            }

            <div>
              <div className="table-responsive">
                <table className="table display-options-table" data-testid="modules-table">
                  {/* <thead ref={theadRef} className="sticky-top">
                    <tr>
                      <th className="v-middle">{editableTranslate('form.fields')}</th>
                      {
                        modules.map((module, index) => <th key={index} className="align-text-top">
                          <span className="d-block font-weight-bold text-big">{editableTranslate('field_configuration.module.' + moduleFieldKey(module))}</span>
                        </th>)
                      }
                    </tr>
                  </thead> */}
                  <tbody>
                    {
                      fetchLoading ? <tr>
                        <td className="text-center" colSpan={modules.length + 1} data-testid="loading">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="sr-only">Wird geladen...</span>
                          </div>
                        </td>
                      </tr> : <>
                        <tr className="tr-no-border-all">
                          <td></td>
                          {
                            modules.map((module, index) => <td key={index}>
                              <div className="d-inline-flex flex-column">
                                <div className="d-flex invisible">
                                  <DummyField />
                                </div>
                                <div className="text-center font-weight-bold">
                                  {editableTranslate('field_configuration.module.' + moduleFieldKey(module))}
                                </div>
                              </div>
                            </td>)
                          }
                        </tr>
                        <tr className="group-title-tr">
                          <td>
                            <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.fixed ? 'up' : 'down'}`} onClick={() => showHideSection('fixed')} data-testid="toggle-section-fixed" />
                            {editableTranslate('employee.basic_data')}
                          </td>
                          {
                            modules.map((_, index) => <td key={index}>
                              {editableTranslate('field_configuration.overwrite_field_label', 'Feldbezeichnung überschreiben')}
                            </td>)
                          }
                        </tr>
                        {
                          showSection?.fixed && filteredFixedFields && filteredFixedFields.map((field, i) => <tr key={i} className={`group-field-row with-bottom-border ${i === filteredFixedFields.length - 1 ? 'last-row' : ''}`}>
                            <td className="v-middle">{editableTranslate(field.translation_key, field.name)}</td>
                            {
                              modules.map((module, j) => {
                                const moduleKey = moduleFieldKey(module);

                                return <td key={j} className={`v-middle`}>
                                {
                                  fieldSupportsModule(field, module) && <div className="d-flex align-items-center">
                                    <div className="d-inline-flex align-items-center mr-3">
                                      <span className="toggle-switch on-off mr-1">
                                        <input id={`field-${field.hash_id}-${moduleKey}`} type="checkbox" value={true} data-testid={`field-${field.hash_id}-${moduleKey}-toggle`} {...register(`fields.${field?.hash_id}.${moduleKey}_enabled`)} readOnly={fixedChecked?.[moduleKey]?.includes(field.slug)} disabled={fixedChecked?.[moduleKey]?.includes(field.slug)} />
                                        <label htmlFor={`field-${field.hash_id}-${moduleKey}`}></label>
                                      </span>
                                      <span>
                                        {
                                          watchFields?.[field.hash_id]?.[moduleKey + '_enabled'] ? editableTranslate('field_configuration.on', 'Aktiv') : editableTranslate('field_configuration.off', 'deaktiviert')
                                        }
                                      </span>
                                    </div>
                                    <Input size="sm" className={`d-inline-block max-w-200 ${watchFields?.[field.hash_id]?.[moduleKey + '_enabled'] ? '' : 'invisible'}`} type="text" {...register(`fields.${field?.hash_id}.${moduleKey}_custom_name`)} maxLength={255} />
                                  </div>
                                }
                              </td>;
                              })
                            }
                          </tr>)
                        }

                        {
                          modules.length === 0 && <tr>
                            <td className="no-border-top" colSpan={1}></td>
                          </tr>
                        }
                        <tr className="tr-no-border-all">
                          <td></td>
                          {
                            modules.map((module, index) => <td key={index}>
                              <div className="d-inline-flex flex-column">
                                <div className="d-flex invisible">
                                  <DummyField />
                                </div>
                                <div className="text-center font-weight-bold">
                                  {editableTranslate('field_configuration.module.' + moduleFieldKey(module))}
                                </div>
                              </div>
                            </td>)
                          }
                        </tr>
                        <tr className="group-title-tr">
                          <td>
                            <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.calculation ? 'up' : 'down'}`} onClick={() => showHideSection('calculation')} data-testid="toggle-section-calculation" />
                            {editableTranslate('employee.calculation_related')}
                          </td>
                          {
                            modules.map((_, index) => <td key={index}>
                              {editableTranslate('field_configuration.overwrite_field_label', 'Feldbezeichnung überschreiben')}
                            </td>)
                          }
                        </tr>
                        {
                          showSection?.calculation && filteredCalculationRelatedFields && filteredCalculationRelatedFields.map((field, i) => <tr key={i} className={`group-field-row with-bottom-border ${i === filteredCalculationRelatedFields.length - 1 ? 'last-row' : ''}`}>
                            <td className="v-middle">{editableTranslate(field.translation_key, field.name)}</td>
                            {
                              modules.map((module, j) => <td key={j} className={`v-middle`}>
                                {
                                  fieldSupportsModule(field, module) && <div className="d-flex align-items-center">
                                    <div className="d-inline-flex align-items-center mr-3">
                                      <span className="toggle-switch on-off mr-1">
                                        <input id={`field-${field.hash_id}-${moduleFieldKey(module)}`} type="checkbox" value={true} data-testid={`field-${field.hash_id}-${moduleFieldKey(module)}-toggle`} {...register(`fields.${field?.hash_id}.${moduleFieldKey(module)}_enabled`)} readOnly={fixedChecked?.[moduleFieldKey(module)]?.includes(field.slug)} disabled={fixedChecked?.[moduleFieldKey(module)]?.includes(field.slug)} />
                                        <label htmlFor={`field-${field.hash_id}-${moduleFieldKey(module)}`}></label>
                                      </span>
                                      <span>
                                        {
                                          watchFields?.[field.hash_id]?.[moduleFieldKey(module) + '_enabled'] ? editableTranslate('field_configuration.on', 'Aktiv') : editableTranslate('field_configuration.off', 'deaktiviert')
                                        }
                                      </span>
                                    </div>
                                    <Input size="sm" className={`d-inline-block max-w-200 ${watchFields?.[field.hash_id]?.[moduleFieldKey(module) + '_enabled'] ? '' : 'invisible'}`} type="text" {...register(`fields.${field?.hash_id}.${moduleFieldKey(module)}_custom_name`)} maxLength={255} />
                                  </div>
                                }
                              </td>)
                            }
                          </tr>)
                        }

                        {
                          filteredStateEmployeeFields.length > 0 && <>
                            {
                              modules.length === 0 && <tr>
                                <td className="no-border-top" colSpan={1}></td>
                              </tr>
                            }
                            <tr className="tr-no-border-all">
                              <td></td>
                              {
                                modules.map((module, index) => <td key={index}>
                                  <div className="d-inline-flex flex-column">
                                    <div className="d-flex invisible">
                                      <DummyField />
                                    </div>
                                    <div className="text-center font-weight-bold">
                                      {editableTranslate('field_configuration.module.' + moduleFieldKey(module))}
                                    </div>
                                  </div>
                                </td>)
                              }
                            </tr>
                            <tr className="group-title-tr">
                              <td>
                                <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.state ? 'up' : 'down'}`} onClick={() => showHideSection('state')} data-testid="toggle-section-state" />
                                {editableTranslate('field_configuration.state_employee_data', 'State employee data')}
                              </td>
                              {
                                modules.map((_, index) => <td key={index}>
                                  {editableTranslate('field_configuration.overwrite_field_label', 'Feldbezeichnung überschreiben')}
                                </td>)
                              }
                            </tr>
                          </>
                        }
                        {
                          showSection?.state && filteredStateEmployeeFields && filteredStateEmployeeFields.map((field, i) => <tr key={i} className={`group-field-row with-bottom-border ${i === filteredStateEmployeeFields.length - 1 ? 'last-row' : ''}`}>
                            <td className="v-middle">{editableTranslate(field.translation_key, field.name)}</td>
                            {
                              modules.map((module, j) => <td key={j} className={`v-middle`}>
                                {
                                  fieldSupportsModule(field, module) && <div className="d-flex align-items-center">
                                    <div className="d-inline-flex align-items-center mr-3">
                                      <span className="toggle-switch on-off mr-1">
                                        <input id={`field-${field.hash_id}-${moduleFieldKey(module)}`} type="checkbox" value={true} data-testid={`field-${field.hash_id}-${moduleFieldKey(module)}-toggle`} {...register(`fields.${field?.hash_id}.${moduleFieldKey(module)}_enabled`)} readOnly={fixedChecked?.[moduleFieldKey(module)]?.includes(field.slug)} disabled={fixedChecked?.[moduleFieldKey(module)]?.includes(field.slug)} />
                                        <label htmlFor={`field-${field.hash_id}-${moduleFieldKey(module)}`}></label>
                                      </span>
                                      <span>
                                        {
                                          watchFields?.[field.hash_id]?.[moduleFieldKey(module) + '_enabled'] ? editableTranslate('field_configuration.on', 'Aktiv') : editableTranslate('field_configuration.off', 'deaktiviert')
                                        }
                                      </span>
                                    </div>
                                    <Input size="sm" className={`d-inline-block max-w-200 ${watchFields?.[field.hash_id]?.[moduleFieldKey(module) + '_enabled'] ? '' : 'invisible'}`} type="text" {...register(`fields.${field?.hash_id}.${moduleFieldKey(module)}_custom_name`)} maxLength={255} />
                                  </div>
                                }
                              </td>)
                            }
                          </tr>)
                        }

                        {
                          modules.length === 0 && <tr>
                            <td className="no-border-top" colSpan={1}></td>
                          </tr>
                        }
                        <tr className="tr-no-border-all">
                          <td></td>
                          {
                            modules.map((module, index) => <td key={index}>
                              <div className="d-inline-flex flex-column">
                                <div className="d-flex invisible">
                                  <DummyField />
                                </div>
                                <div className="text-center font-weight-bold">
                                  {editableTranslate('field_configuration.module.' + moduleFieldKey(module))}
                                </div>
                              </div>
                            </td>)
                          }
                        </tr>
                        <tr className="group-title-tr">
                          <td>
                            <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.lk ? 'up' : 'down'}`} onClick={() => showHideSection('lk')} data-testid="toggle-section-lk" />
                            {editableTranslate('employee.balanced_scorecard')}
                          </td>
                          {
                            modules.map((_, index) => <td key={index}>
                              {editableTranslate('field_configuration.overwrite_field_label', 'Feldbezeichnung überschreiben')}
                            </td>)
                          }
                        </tr>
                        {
                          showSection?.lk && filteredLkFields && filteredLkFields.map((field, i) => <tr key={i} className={`group-field-row with-bottom-border ${i === filteredLkFields.length - 1 ? 'last-row' : ''}`}>
                            <td className="v-middle">{editableTranslate(field.translation_key, field.name)}</td>
                            {
                              modules.map((module, j) => <td key={j} className={`v-middle`}>
                                {
                                  fieldSupportsModule(field, module) && <div className="d-flex align-items-center">
                                    <div className="d-inline-flex align-items-center mr-3">
                                      <span className="toggle-switch on-off mr-1">
                                        <input id={`field-${field.hash_id}-${moduleFieldKey(module)}`} type="checkbox" value={true} data-testid={`field-${field.hash_id}-${moduleFieldKey(module)}-toggle`} {...register(`fields.${field?.hash_id}.${moduleFieldKey(module)}_enabled`)} readOnly={fixedChecked?.[moduleFieldKey(module)]?.includes(field.slug)} disabled={fixedChecked?.[moduleFieldKey(module)]?.includes(field.slug)} />
                                        <label htmlFor={`field-${field.hash_id}-${moduleFieldKey(module)}`}></label>
                                      </span>
                                      <span>
                                        {
                                          watchFields?.[field.hash_id]?.[moduleFieldKey(module) + '_enabled'] ? editableTranslate('field_configuration.on', 'Aktiv') : editableTranslate('field_configuration.off', 'deaktiviert')
                                        }
                                      </span>
                                    </div>
                                    <Input size="sm" className={`d-inline-block max-w-200 ${watchFields?.[field.hash_id]?.[moduleFieldKey(module) + '_enabled'] ? '' : 'invisible'}`} type="text" {...register(`fields.${field?.hash_id}.${moduleFieldKey(module)}_custom_name`)} maxLength={255} />
                                  </div>
                                }
                              </td>)
                            }
                          </tr>)
                        }

                        {
                          filteredFreeFields.length > 0 && <>
                            {
                              modules.length === 0 && <tr>
                                <td className="no-border-top" colSpan={1}></td>
                              </tr>
                            }
                            <tr className="tr-no-border-all">
                              <td></td>
                              {
                                modules.map((module, index) => <td key={index}>
                                  <div className="d-inline-flex flex-column">
                                    <div className="d-flex invisible">
                                      <DummyField />
                                    </div>
                                    <div className="text-center font-weight-bold">
                                      {editableTranslate('field_configuration.module.' + moduleFieldKey(module))}
                                    </div>
                                  </div>
                                </td>)
                              }
                            </tr>
                            <tr className="group-title-tr">
                              <td>
                                <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.free ? 'up' : 'down'}`} onClick={() => showHideSection('free')} data-testid="toggle-section-free" />
                                {editableTranslate('employee.additional_data')}
                              </td>
                              {
                                modules.map((_, index) => <td key={index}>
                                  {editableTranslate('field_configuration.overwrite_field_label', 'Feldbezeichnung überschreiben')}
                                </td>)
                              }
                            </tr>
                          </>
                        }
                        {
                          showSection?.free && filteredFreeFields && filteredFreeFields.map((field, i) => <tr key={i} className={`group-field-row with-bottom-border ${i === filteredFreeFields.length - 1 ? 'last-row' : ''}`}>
                            <td className="v-middle">{editableTranslate(field.translation_key, field.name)}</td>
                            {
                              modules.map((module, j) => <td key={j} className={`v-middle`}>
                                {
                                  fieldSupportsModule(field, module) && <div className="d-flex align-items-center">
                                    <div className="d-inline-flex align-items-center mr-3">
                                      <span className="toggle-switch on-off mr-1">
                                        <input id={`field-${field.hash_id}-${moduleFieldKey(module)}`} type="checkbox" value={true} data-testid={`field-${field.hash_id}-${moduleFieldKey(module)}-toggle`} {...register(`fields.${field?.hash_id}.${moduleFieldKey(module)}_enabled`)} readOnly={fixedChecked?.[moduleFieldKey(module)]?.includes(field.slug)} disabled={fixedChecked?.[moduleFieldKey(module)]?.includes(field.slug)} />
                                        <label htmlFor={`field-${field.hash_id}-${moduleFieldKey(module)}`}></label>
                                      </span>
                                      <span>
                                        {
                                          watchFields?.[field.hash_id]?.[moduleFieldKey(module) + '_enabled'] ? editableTranslate('field_configuration.on', 'Aktiv') : editableTranslate('field_configuration.off', 'deaktiviert')
                                        }
                                      </span>
                                    </div>
                                    <Input size="sm" className={`d-inline-block max-w-200 ${watchFields?.[field.hash_id]?.[moduleFieldKey(module) + '_enabled'] ? '' : 'invisible'}`} type="text" {...register(`fields.${field?.hash_id}.${moduleFieldKey(module)}_custom_name`)} maxLength={255} />
                                  </div>
                                }
                              </td>)
                            }
                          </tr>)
                        }
                      </>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
